import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/Shared";
import { fmtDateTime, fmtFCFA, ROLES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Check, X, Clock, Briefcase, Users, TrendingUp, CheckSquare, FileSignature } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  project: { label: "Project", icon: Briefcase, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  employee: { label: "Employee", icon: Users, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  income: { label: "Income", icon: TrendingUp, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  check: { label: "Payment Check", icon: CheckSquare, cls: "bg-amber-50 text-amber-700 border-amber-200" },
};
const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

export default function Approvals() {
  const { isRole } = useAuth();
  const isCeo = isRole(ROLES.CEO);
  const [busy, setBusy] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [note, setNote] = useState("");

  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["approvals"], queryFn: () => api.get("/approvals").then((r) => r.data) });
  const { data: subContracts = [] } = useQuery({ queryKey: ["sub-contracts"], queryFn: () => api.get("/sub-contracts").then((r) => r.data), enabled: isCeo });
  const load = () => qc.invalidateQueries();

  const pending = rows.filter((r) => r.status === "pending");
  const history = rows.filter((r) => r.status !== "pending");
  const pendingSC = subContracts.filter((c) => c.status === "pending");

  const [scReject, setScReject] = useState(null);
  const [scNote, setScNote] = useState("");
  const validateSC = async (c, action, note = "") => {
    setBusy(c.id);
    try {
      await api.patch(`/sub-contracts/${c.id}/validate`, { action, note });
      toast.success(action === "approve" ? "Sub-contract validated" : "Sub-contract rejected");
      setScReject(null); setScNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  const approve = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/approvals/${r.id}/approve`, { note: "" });
      toast.success(`${TYPE_META[r.entity_type]?.label || "Request"} approved & registered`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  const doReject = async () => {
    if (!rejectFor) return;
    setBusy(rejectFor.id);
    try {
      await api.post(`/approvals/${rejectFor.id}/reject`, { note: note || "" });
      toast.success("Request rejected");
      setRejectFor(null); setNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };

  return (
    <div data-testid="approvals-page">
      <PageHeader
        title="Approvals"
        subtitle={isCeo ? "Review and decide on requests submitted by the Assistant CEO" : "Track the status of the requests you submitted for CEO approval"}
      />

      {/* Pending */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
          <Clock className="h-4 w-4 text-amber-500" /> Pending {isCeo ? "your decision" : "CEO decision"} ({pending.length})
        </div>
        {pending.length === 0 ? (
          <EmptyState text={isCeo ? "No requests awaiting your approval." : "You have no pending requests."} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pending.map((r) => {
              const T = TYPE_META[r.entity_type] || {};
              const Icon = T.icon || Clock;
              return (
                <div key={r.id} data-testid={`approval-row-${r.id}`} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${T.cls}`}>
                      <Icon className="h-3.5 w-3.5" /> {T.label || r.entity_type}
                    </span>
                    <span className="text-xs text-slate-400">{fmtDateTime(r.created_at)}</span>
                  </div>
                  <div className="mt-3 text-slate-900 font-semibold" data-testid={`approval-summary-${r.id}`}>{r.summary || "—"}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Business unit: <b className="text-slate-700">{r.company_name || "—"}</b> · Requested by <b className="text-slate-700">{r.requested_by_name}</b>
                  </div>
                  {isCeo && (
                    <div className="mt-4 flex items-center gap-2">
                      <Button size="sm" disabled={busy === r.id} data-testid={`approve-approval-${r.id}`}
                        onClick={() => approve(r)} className="bg-emerald-600 hover:bg-emerald-700">
                        <Check className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy === r.id} data-testid={`reject-approval-${r.id}`}
                        onClick={() => { setRejectFor(r); setNote(""); }} className="text-red-600 border-red-200 hover:bg-red-50">
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pending sub-contracts (CEO validation) */}
      {isCeo && (
        <div className="mb-8" data-testid="approvals-subcontracts">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
            <FileSignature className="h-4 w-4 text-blue-500" /> Sub-contracts awaiting your validation ({pendingSC.length})
          </div>
          {pendingSC.length === 0 ? (
            <EmptyState text="No sub-contracts awaiting your validation." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pendingSC.map((c) => (
                <div key={c.id} data-testid={`approval-subcontract-${c.id}`} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
                      <FileSignature className="h-3.5 w-3.5" /> Sub-Contract
                    </span>
                    <span className="text-xs text-slate-400">{fmtDateTime(c.created_at)}</span>
                  </div>
                  <div className="mt-3 text-slate-900 font-semibold">{c.subcontractor_name || "—"}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Project: <b className="text-slate-700">{c.project_name || "—"}</b> · Prepared by <b className="text-slate-700">{c.created_by_name}</b>{c.created_by_role ? ` (${c.created_by_role})` : ""}
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs">
                    <span className="text-purple-700">Cost: <b>{fmtFCFA(c.cost)}</b></span>
                    <span className="text-amber-600">Advance: <b>{fmtFCFA(c.advance)}</b></span>
                    <span className="text-emerald-700">Balance: <b>{fmtFCFA(c.balance)}</b></span>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Button size="sm" disabled={busy === c.id} data-testid={`approve-subcontract-${c.id}`}
                      onClick={() => validateSC(c, "approve")} className="bg-emerald-600 hover:bg-emerald-700">
                      <Check className="h-4 w-4 mr-1" /> Validate
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy === c.id} data-testid={`reject-subcontract-${c.id}`}
                      onClick={() => { setScReject(c); setScNote(""); }} className="text-red-600 border-red-200 hover:bg-red-50">
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-sm font-semibold text-slate-700 mb-3">Decision History</div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {history.length === 0 ? <EmptyState text="No decided requests yet." /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead><TableHead>Details</TableHead><TableHead>Business Unit</TableHead>
                  <TableHead>Requested By</TableHead><TableHead>Status</TableHead>
                  <TableHead>Decided By</TableHead><TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((r) => (
                  <TableRow key={r.id} data-testid={`history-row-${r.id}`}>
                    <TableCell className="text-slate-700">{TYPE_META[r.entity_type]?.label || r.entity_type}</TableCell>
                    <TableCell className="text-slate-800 font-medium max-w-[220px] truncate">{r.summary || "—"}
                      {r.decision_note && <div className="text-xs text-slate-400 italic truncate">“{r.decision_note}”</div>}
                    </TableCell>
                    <TableCell className="text-slate-600">{r.company_name || "—"}</TableCell>
                    <TableCell className="text-slate-600">{r.requested_by_name}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_CLS[r.status]}`} data-testid={`history-status-${r.id}`}>{r.status}</span>
                    </TableCell>
                    <TableCell className="text-slate-600">{r.decided_by_name || "—"}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{r.decided_at ? fmtDateTime(r.decided_at) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Rejecting <b>{rejectFor?.summary}</b> ({TYPE_META[rejectFor?.entity_type]?.label}). It will be cancelled and saved as rejected.</p>
            <div className="space-y-1.5">
              <Label className="text-sm">Reason <span className="text-xs font-normal text-slate-400">· optional</span></Label>
              <Textarea data-testid="reject-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Let the Assistant CEO know why…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button data-testid="reject-confirm" onClick={doReject} className="bg-red-600 hover:bg-red-700">Reject request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scReject} onOpenChange={(o) => !o && setScReject(null)}>
        <DialogContent data-testid="subcontract-reject-dialog">
          <DialogHeader><DialogTitle>Reject sub-contract</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Rejecting the sub-contract for <b>{scReject?.subcontractor_name}</b> ({scReject?.project_name}). The preparer will be notified.</p>
            <div className="space-y-1.5">
              <Label className="text-sm">Reason <span className="text-xs font-normal text-slate-400">· optional</span></Label>
              <Textarea data-testid="subcontract-reject-note" value={scNote} onChange={(e) => setScNote(e.target.value)} placeholder="Let the preparer know why…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScReject(null)}>Cancel</Button>
            <Button data-testid="subcontract-reject-confirm" onClick={() => validateSC(scReject, "reject", scNote)} className="bg-red-600 hover:bg-red-700">Reject sub-contract</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
